import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { translateCol } from "../../../../custom/columnI18n";
import useVisinaTabele from "../../../../custom/visinaTabele";
import usePamcenjePregleda from "../../../../custom/pamcenjePregleda";
import ZaposleniFilter, { PRAZAN_FILTER, primeniFilter } from './ZaposleniFilter';
import { useTranslation } from "react-i18next";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { AuthContext } from "../../../../Header/auth - context";
import { uzmiGresku } from '../../../../custom/greske';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import { Tooltip } from 'primereact/tooltip';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { confirmDialog } from 'primereact/confirmdialog';
import { ConfirmDialog } from 'primereact/confirmdialog';

const ZaposleniPregled = () => {
  const { t } = useTranslation();
  const toast = useRef(null);
  const auth = useContext(AuthContext);
  const company = useSelector(state => state.company);
  const user = useSelector(state => state.user)
  const employee = useSelector(state => state.employee);
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState(null);
  /* Strana, sort, pretraga i kvačica za neaktivne se pamte i posle odlaska na
     Unos ili Dokumente — podrazumevani prikaz (redosled sa servera: prezime
     rastuće, prva strana) vidi se samo pri prvom ulasku. Sort se ne postavlja
     ovde: spisak već stiže poređan iz baze. */
  const [pregled, postaviPregled] = usePamcenjePregleda(
    `zaposleni-pregled:${company?.companyId}`,
    { first: 0, sortField: null, sortOrder: null, pretraga: '', prikaziNeaktivne: false,
      filter: PRAZAN_FILTER }
  );
  const { first, sortField, sortOrder, prikaziNeaktivne } = pregled;
  /* Stariji zapamćeni zapis (od pre uvođenja filtera) nema `filter` — bez
     zaštite bi filtriranje puklo na prvom ulasku posle nadogradnje. */
  const filter = pregled.filter || PRAZAN_FILTER;
  const globalFilter = pregled.pretraga || null;
  /* Brojač koji tera ponovno učitavanje spiska posle reaktivacije — bez njega bi
     zaposleni ostao "Pasivan" na ekranu iako je u bazi vraćen u rad. */
  const [osveziSpisak, setOsveziSpisak] = useState(0);
  const [allColumns, setAllColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [originalColumnOrder, setOriginalColumnOrder] = useState([]);
  /* Izbor se ne pravi iznova pri svakom ulasku: zaposleni izabran ovde ili na
     bilo kom drugom tabu stoji u zajedničkom stanju (redux `employee`), pa se
     ovde odmah vidi kao označen red. */
  const [selectedRowId , setSelectedRowId] = useState(employee ?? null);

  /* Spisak sa servera nosi i aktivne i pasivne — filtriranje je ovde, jer je
     podatak već tu i ne treba novi poziv pri svakom prebacivanju kvačice.
     Kolona "Status" je tekst iz upita ('Aktivan' / 'Pasivan'). */
  const zaPrikaz = useMemo(() => {
    if (tableData == null) return null;
    /* Kvačica bira IZ ČEGA se filtrira, napredni filter bira ŠTA ostaje.
       Izuzetak: kad filter sam traži pasivne, kvačica bi mu pojela ceo
       rezultat, pa se u tom slučaju polazi od punog spiska. */
    const trazenoPasivne = filter.status === 'PASIVAN';
    const osnova = (prikaziNeaktivne || trazenoPasivne)
      ? tableData
      : tableData.filter(r => r?.Status !== 'Pasivan');
    return primeniFilter(osnova, filter);
  }, [tableData, prikaziNeaktivne, filter]);

  /* Zapamćena strana ume da ostane iza kraja spiska (neko je u međuvremenu
     obrisan, kvačica za neaktivne skratila spisak) — tada se vraća na prvu,
     da tabela ne ostane prazna. */
  useEffect(() => {
    if (zaPrikaz && first > 0 && first >= zaPrikaz.length) {
      postaviPregled({ first: 0 });
    }
  }, [zaPrikaz, first, postaviPregled]);

  /* Red koji je trenutno izabran — po njemu se zna da li nuditi reaktivaciju. */
  const izabraniRed = useMemo(
    () => (tableData || []).find(r => String(r?.Izaberi) === String(selectedRowId)) || null,
    [tableData, selectedRowId]
  );
  const izabraniJePasivan = izabraniRed?.Status === 'Pasivan';

  const getId = (row) => row?.ID ?? row?.id ?? row?.Izaberi ?? null; // ➜ stabilan ključ
  const isSelected = (row) => String(getId(row)) === String(selectedRowId);
  const [izabran, setIzabran] = useState(!!employee)

  /* Zaposleni izabran ranije ume da nestane iz spiska (promenjena firma, obrisan
     je) — tada se izbor pušta, da ne ostane označen niko. */
  useEffect(() => {
    if (!tableData || selectedRowId == null) return;
    const postoji = tableData.some(r => String(getId(r)) === String(selectedRowId));
    if (!postoji) {
      setSelectedRowId(null);
      setIzabran(false);
      dispatch({ type: "EMPLOYEE", value: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, selectedRowId]);

  /* Spisak se zavrsava na dnu ekrana; visina se meri, ne pogadja.
     Zavisnosti su ono sto pomera tabelu nanize: prelom trake sa kolonama,
     poruka o selekciji i kvacica za neaktivne. */
  const [omotTabele, visinaTabele, redovaTabele] = useVisinaTabele([zaPrikaz, selectedColumns, izabran]);

  // Path normalization and document location helper
  const getDocumentLocation = (path) => {
    if (!path) return null;
    
    // Normalize Windows path to URL format
    const normalizedPath = path.replace(/\\/g, '/');
    
    // Remove full C:/Dokumenta/ from path
    const relativePath = normalizedPath.replace(/^C:\/Dokumenta\//, '');
    const encodedPath = encodeURI(relativePath); // Encode spaces, etc.
    return `${process.env.REACT_APP_BACKEND_URL}/files/${encodedPath}`;
  };

  const handleErrors = (response) => {
    if (response.ok) {
      return response.json();
    } else {
      throw response;
    }
  };

  const getConfigColumns = async () => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + auth.token,
    },
    body: JSON.stringify({
      id_config: 264
    }),
  };

    const data = await fetch(process.env.REACT_APP_BACKEND_URL + "/config/", requestOptions)
      .then(handleErrors); 

    if (data.message) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: data.message,
        life: 3000,
      });
      return { empty: true, data: [] };
    }

     if(data.length > 0) {
                    return { empty: false, data: data };
                } else {
                    return { empty: true, data: [] };
                }


};

  useEffect(() => {

    /* Izabrani zaposleni se NE poništava pri ulasku u Pregled — nosi se kroz
       sve tabove (Unos, Dokumenti, AETR, Transfer, Radni odnos), pa mora da
       preživi i povratak ovde. Red se ispod, po dolasku spiska, samo označi. */

    let requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
      }
    };
    
    fetch(process.env.REACT_APP_BACKEND_URL + "/employees/returnAllDataEmployees", requestOptions)
      .then(handleErrors)
      .then(async (data) => {
        setTableData(data);
        
        // Extract all available columns from the first data row
            if (data && data.length > 0) {
        const columns = Object.keys(data[0])
            .filter(key => !['ID'].includes(key))
            .map(key => ({
            label: key,
            value: key
            }));
  

          setAllColumns(columns);
          const originalOrder = Object.keys(data[0]);
          setOriginalColumnOrder(originalOrder);
          
          const columnsDB = await getConfigColumns();

              if (!columnsDB.empty && columnsDB.data.length > 0) {
                const arr = JSON.parse(columnsDB.data[0].parametri);
                setSelectedColumns(arr);
              } else {
                setSelectedColumns(columns.slice(0, 5).map(col => col.value));
              }
        }
      })
      .catch((error) => {
        error.json().then((errorMessage) => {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: errorMessage.message,
            life: 3000,
          });
          console.log(errorMessage);
        });
      });
  }, [auth.token, company.companyId, osveziSpisak]);

  
  /* Pasivizacija (meko brisanje) stoji ovde, a ne na Unosu: Pregled i Unos su
     dva odvojena prikaza koja ne dele state, a izbor zaposlenog živi u tabeli —
     pa je radnja nad izabranim redom na istom mestu gde se red i bira.
     Pandan je `reaktivirajZaposlenog` ispod; dugmad se nikad ne vide zajedno. */
  const pasivizirajZaposlenog = () => {
    const ime = [izabraniRed?.['Prezime'], izabraniRed?.['Ime']].filter(Boolean).join(' ');
    confirmDialog({
      message: t('admin.potvrdaPasivizacijeZaposlenog', { ime }),
      acceptLabel: t('common.da'),
      rejectLabel: t('common.ne'),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          const odgovor = await fetch(process.env.REACT_APP_BACKEND_URL + "/employees/DeleteEmployee", {
            method: "DELETE",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify({ id: selectedRowId }),
          });
          if (!odgovor.ok) throw odgovor;

          /* Telo ume da bude prazno — isto kao kod reaktivacije, pa se prvo čita tekst. */
          const telo = await odgovor.text();
          const podaci = telo ? JSON.parse(telo) : [];
          if (podaci?.[0]?.message) {
            toast.current.show({ severity: "warn", summary: t('admin.m_obavestenje_1'),
                                 detail: podaci[0].message, life: 4000 });
            return;
          }

          setSelectedRowId(null);
          setIzabran(false);
          dispatch({ type: "EMPLOYEE", value: null });
          setOsveziSpisak(k => k + 1);

          /* Mobilni nalog se gasi u istom pozivu (druga baza), pa odgovor nosi i ishod
             tog dela. Kaže se izričito: admin mora da zna da li je čovek stvarno
             izbačen sa telefona ili mu pristup i dalje radi. */
          if (podaci?.mobilniBlokiran) {
            toast.current.show({ severity: "success",
                                 summary: t('admin.m_uspesno_pasiviziranje_podataka_zaposleno'),
                                 detail: t('admin.mobilniNalogBlokiran'), life: 5000 });
          } else if (podaci?.mobilniGreska) {
            toast.current.show({ severity: "warn",
                                 summary: t('admin.m_uspesno_pasiviziranje_podataka_zaposleno'),
                                 detail: t('admin.mobilniNalogNijeBlokiran'), life: 8000 });
          } else {
            toast.current.show({ severity: "success",
                                 summary: t('admin.m_uspesno_pasiviziranje_podataka_zaposleno'), life: 3000 });
          }
        } catch (e) {
          const g = await uzmiGresku(e, t('common.greska'));
          toast.current.show({ severity: "error", summary: t('common.greska'), detail: g.message, life: 5000 });
        }
      },
    });
  };

  const reaktivirajZaposlenog = () => {
    confirmDialog({
      message: `Jel ste sigurni da želite reaktivirati zaposlenog ${[izabraniRed?.['Prezime'], izabraniRed?.['Ime']].filter(Boolean).join(' ')}?`,
      acceptLabel: "Da",
      rejectLabel: "Ne",
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-success",
      accept: async () => {
        try {
          const odgovor = await fetch(process.env.REACT_APP_BACKEND_URL + "/employees/ReactivateEmployee", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify({ id: selectedRowId }),
          });
          if (!odgovor.ok) throw odgovor;

          /* Telo ume da bude prazno (radnja je prošla, nema šta da se vrati), a
             odgovor.json() nad praznim telom baca grešku — pa se prvo čita tekst. */
          const telo = await odgovor.text();
          const podaci = telo ? JSON.parse(telo) : [];

          /* Procedura vrati poruku samo kad zaposleni nije nađen u firmi. */
          if (podaci?.[0]?.message) {
            toast.current.show({ severity: "warn", summary: t('admin.m_obavestenje_1'),
                                 detail: podaci[0].message, life: 4000 });
            return;
          }

          /* Izbor se poništava: zaposleni više nije pasivan, pa bi dugme ostalo
             da stoji nad zapisom nad kojim nema šta da radi. */
          setSelectedRowId(null);
          setIzabran(false);
          dispatch({ type: "EMPLOYEE", value: null });
          setOsveziSpisak(k => k + 1);

          toast.current.show({ severity: "success", summary: t('admin.m_uspesna_reaktivacija'), life: 3000 });
        } catch (e) {
          const g = await uzmiGresku(e, t('admin.m_reaktivacija_greska'));
          toast.current.show({ severity: "error", summary: t('common.greska'), detail: g.message, life: 5000 });
        }
      },
    });
  };

  const chooseEmployee = (rowData) => {    
    const id = getId(rowData);

    setSelectedRowId((prev) => {
      const next = String(prev) === String(id) ? null : String(id);

    if (next) {
      // selekcija
      const employeeID = tableData?.find(t => String(t.Izaberi) === String(rowData.Izaberi));
      if (employeeID) {
    
        setIzabran(true);
      }
      dispatch({ type: "EMPLOYEE", value: rowData.Izaberi });
    } else {
      // deselekcija
      setIzabran(false);
      dispatch({ type: "EMPLOYEE", value: null });
    }

    return next;
  });
  }


   const previewBodyTemplate = (rowData, field) => {
    const filePathsString = rowData[field];
    if (!filePathsString) return '-';
  
       if(field == 'Izaberi') {
    
          const selected = isSelected(rowData);
           return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Button
                        label='Izaberi'
                        icon="pi pi-eye"
                        className="p-button-text p-button-sm"
                        onClick={(e) => chooseEmployee(rowData)}
                        tooltip="Izaberi putni nalog za dalji rad"
                        tooltipOptions={{ position: 'top' }}
                      />
                    
                </div>
              );
       } else {

    const filePaths = filePathsString.split('|').filter(p => p.trim() !== '');
  
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {filePaths.map((path, idx) => {
          const documentUrl = getDocumentLocation(path);
          if (!documentUrl) return null;
  
          // Izdvoji ime fajla bez ekstenzije
          const fileNameWithExt = path.split('/').pop(); // npr. TOI-Psihologija-2016-testkljuc_20250726222909703.pdf
          const fileName = fileNameWithExt.replace(/\.(pdf|jpg|png)$/i, '');
  
          return (
            <Button
              key={idx}
              label={fileName}
              icon="pi pi-eye"
              className="p-button-text p-button-sm"
              onClick={() => window.open(documentUrl, '_blank')}
              tooltip="Pregledaj dokument"
              tooltipOptions={{ position: 'top' }}
            />
          );
        })}
      </div>
    ); }
  };

  const isFileColumn = (field) => {
    return field.startsWith('Fajl') || 
           field.includes('Izaberi') || 
           field.includes('fajl') || 
           field.includes('upload') ||
           field.endsWith('LK') || // Lična karta
           field.endsWith('VD') || // Vozačka dozvola
           field.endsWith('PASS') || // Pasoš
           field.endsWith('VIZA') || // Viza
           field.includes('Dokument'); // Generic document
  };

const exportExcel = async () => {
    if (!selectedColumns || selectedColumns.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Zaposleni');

    // Use selectedColumns instead of all table keys
    const headers = selectedColumns.map(c => translateCol(t, c));

    // Add header row
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFEFEFEF' }
        };
        cell.border = {
            top: { style: 'medium' },
            left: { style: 'medium' },
            bottom: { style: 'medium' },
            right: { style: 'medium' }
        };
    });

    // Add filtered data rows
    (zaPrikaz || []).forEach((rowData) => {
        const row = worksheet.addRow(headers.map(h => rowData[h] ?? ""));
        row.eachCell((cell) => {
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });
    });

    // Auto-size columns
    worksheet.columns.forEach((column, i) => {
        const lengths = [
            headers[i].length,
            ...(zaPrikaz || []).map(row => (row[headers[i]] ?? "").toString().length)
        ];
        column.width = Math.max(...lengths) + 2;
    });

    // Serbian-formatted datetime
    const date = new Date();
    const options = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Belgrade'
    };
    const serbianDateTime = date.toLocaleString('sr-RS', options)
        .replace(/\s/g, ' ')
        .replace(/,/g, '');

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `Zaposleni - ${company.companyName} - ${serbianDateTime}.xlsx`);
};
     /* Zaglavlje tabele nosi i brojače i filter — tako stoje UZ spisak na koji
        se odnose, a ne kao zasebna traka iznad koja gura tabelu naniže. */


  const handleSaveConfig = () => {
         confirmDialog({
                    message: "Jel ste sigurni da želite sačuvati konfiguraciju?",
                    acceptLabel: "Da",
                    rejectLabel: "Ne",
                    icon: "pi pi-info-circle",
                    acceptClassName: "p-button-danger",
                    accept: acceptInsertConfig,
                    reject: rejectInsertConfig,
                    });
  }

  const acceptInsertConfig  = () => {
    if(selectedColumns.length > 0) {
       let requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
      },
      body: JSON.stringify({
        id_config: 264,
        parametri: selectedColumns
      }),
    };
    
    fetch(process.env.REACT_APP_BACKEND_URL + "/config/changeConfig", requestOptions)
      .then(handleErrors)
      .then((data) => {


          if(data.message && data.message == 'Nema izmena u podacima konfiguracije pregleda.'){
                toast.current.show({
                    severity: "warn",
                    summary: t('admin.m_upozorenje_2'),
                    detail: data.message,
                    life: 3000,
                    });
                return
                }  
  
            toast.current.show({
                severity: "success",
                summary: t('admin.m_uspesna_izmena_konfiguracije_pregleda_za_4'),
                life: 3000,
            });

      })
      .catch((error) => {
        error.json().then((errorMessage) => {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: errorMessage.message,
            life: 3000,
          });
          console.log(errorMessage);
        });
      });
    } else {
        toast.current.show({
                    severity: "warn",
                    summary: t('admin.m_upozorenje_2'),
                    detail: t('admin.m_morate_imati_barem_1_izabranu_stavku'),
                    life: 3000,
                    });
    }
     
  }
  const rejectInsertConfig = () => {
    console.log('Config insert rejected!')
  }

     /* Celo upravljanje spiskom stoji u JEDNOM redu zaglavlja tabele, redom:
        filter → izvoz → izbor kolona → kvačica za neaktivne → pretraga skroz
        desno. Ranije je pola toga bilo u zasebnoj traci iznad, koja je gurala
        tabelu naniže. */
     const header = (
        <div className="zap-zaglavlje">
            <ZaposleniFilter
                sviRedovi={tableData}
                filter={filter}
                postaviFilter={(novi) => postaviPregled({ filter: novi, first: 0 })}
                prikazano={(zaPrikaz || []).length}
            />

            <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />

            {/* Pasivizacija izabranog. Stoji samo za AKTIVNOG — pasivnog nema
                smisla pasivizirati, a njemu se na redu iznad tabele nudi
                reaktivacija. */}
            {izabran && !izabraniJePasivan && (
              <Button type="button" icon="pi pi-trash" severity="danger" rounded
                      onClick={pasivizirajZaposlenog} data-pr-tooltip={t('common.delete')} />
            )}

            {allColumns.length > 0 && (
              <div className="zap-kolone">
                <MultiSelect
                  value={selectedColumns}
                  options={allColumns}
                  onChange={(e) => {
                    const sorted = originalColumnOrder.filter((key) => e.value.includes(key));
                    setSelectedColumns(sorted);
                  }}
                  optionLabel="label"
                  /* NAMERNO bez `display="chip"`. U chip prikazu PrimeReact,
                     kad je izabrano više od `maxSelectedLabels`, labeli ne daje
                     ni klasu `p-multiselect-chip` ni `p-multiselect-items-label`
                     — ostaje nestilizovana i u zatvorenom stanju izgleda prazno.
                     Bez chipova ide obična labela koju tema stilizuje, pa se
                     „N kolona odabrano" uvek vidi. */
                  placeholder={t('nova.odaberiteKolone')}
                  showSelectAll={true}
                  filter
                  filterPlaceholder={t('nova.pretraziKolone')}
                  maxSelectedLabels={1}
                  selectedItemsLabel={t('nova.kolonaOdabrano')}
                />
                <button onClick={handleSaveConfig} className="configSaveButton" title={t('nova.sacuvajKonfiguraciju')}>
                  <i className="pi pi-save"></i>
                </button>
              </div>
            )}

            {/* Podrazumevano se vide samo aktivni; kvačica dodaje i pasivne. */}
            <div className="zap-neaktivni">
              <Checkbox inputId="prikaziNeaktivne" checked={prikaziNeaktivne}
                        onChange={(e) => postaviPregled({ prikaziNeaktivne: e.checked, first: 0 })} />
              <label htmlFor="prikaziNeaktivne">{t('admin.neaktivni')}</label>
            </div>

            <input
              type="search"
              className="p-inputtext p-component zap-pretraga"
              defaultValue={pregled.pretraga || ''}
              onInput={(e) => postaviPregled({ pretraga: e.target.value, first: 0 })}
              placeholder={t('nova.phPretraga')}
            />
        </div>
    );

  return (
    <div className="zaposleni_table_container">
      <Toast ref={toast} />
      <ConfirmDialog />
      {/* Red iznad tabele stoji SAMO za pasivnog zaposlenog, da mu se ponudi
          radnja koja ga vraća u rad. Za aktivnog nema šta da se kaže — da je
          izabran vidi se po označenom redu u tabeli i po popunjenoj formi.
          Uslov je na celom redu, ne na sadržaju: .zap-poruka-red ima
          margin-bottom, pa bi prazan div ostavio prazninu iznad tabele. */}
      {izabran && izabraniJePasivan && (
        <div className="zap-poruka-red">
          <Button label={t('admin.reaktiviraj')} icon="pi pi-replay" severity="success"
                  className="zaposleniShift" onClick={reaktivirajZaposlenog}
                  style={{ margin: 0 }} />
        </div>
      )}

      {/* Zaglavlje tabele je .zap-zaglavlje, ne .export-buttons — bez ovog cilja
          `data-pr-tooltip` na dugmadima u njemu (XLS, Obriši) ne bi radio. */}
      <Tooltip target=".zap-zaglavlje>button" position="bottom" />
      <Tooltip target=".export-buttons>button" position="bottom" />
      <div ref={omotTabele}>
      <DataTable
        className="stavke-tabela"
        value={zaPrikaz}
        paginator
        rows={redovaTabele}
        first={first}
        globalFilter={globalFilter}
        onPage={(e) => postaviPregled({ first: e.first })}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={(e) => postaviPregled({ sortField: e.sortField, sortOrder: e.sortOrder, first: 0 })}
        scrollable
        header={header}
        scrollHeight={visinaTabele}
        emptyMessage={t('common.noDataTable')}
        loading={zaPrikaz === null}
        resizableColumns
        columnResizeMode="expand"
        /* Pasivan zaposleni se izdvaja crvenim redom; izbor reda ima prednost. */
        rowClassName={(data) =>
          isSelected(data)
            ? "selected-row"
            : (data?.Status === "Pasivan" ? "row-invoice-overdue" : "")
        }
      >
        {selectedColumns.map((colKey) => {
        const colMeta = allColumns.find(col => col.value === colKey);
        return (
          <Column
            key={colKey}
            field={colKey}
            header={translateCol(t, colMeta?.label || colKey)}
            sortable
            body={(rowData) => {
              if (isFileColumn(colKey)) {
                return previewBodyTemplate(rowData, colKey);
              }
              return rowData[colKey] || '-';
            }}
            style={{ minWidth: '150px'}}
          />
        );
      })}
      </DataTable>
      </div>
    </div>
  );
};

export default ZaposleniPregled;